import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MainLayoutDetail from "../../components/layout/MainLayoutDetail";
import SectionBox from "../../components/layout/SectionBox";
import Button from "../../components/atoms/Button";
import { useRegisterStore } from "../../store/useRegisterStore";
import UserAgreementModal from "../../components/modals/UserAgreementModal";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "react-toastify";
// @ts-ignore
import ReCAPTCHA from "react-google-recaptcha";

export default function Register() {
  const [role, setRole] = useState<"company" | "candidate" | null>(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [agree, setAgree] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [canAgree, setCanAgree] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);


  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
  };

  const location = useLocation();
  const navigate = useNavigate();
  const currentLang = location.pathname.startsWith("/id") ? "id" : "en";

  const { registerCandidate, registerCompany, loading } =
    useRegisterStore();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!role) {
      toast.error("Please select role");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Password confirmation does not match");
      return;
    }

    if (!agree) {
      toast.error("Please read and confirm to the User Agreement");
      return;
    }


    if (!captchaToken) {
      toast.error("Please verify that you are not a robot");
      return;
    }

    const payload = {
      email: formData.email,
      password: formData.password,
      password_confirmation: formData.confirmPassword,
    };

    try {
      if (role === "candidate") {
        await registerCandidate(payload);
      }

      if (role === "company") {
        await registerCompany(payload);
      }

      navigate(`/${currentLang}/login`);
    } catch (error) {
      toast.error("Register failed");
    }
  };

  /* ================= ROLE SELECTION ================= */
  if (!role) {
    return (
      <MainLayoutDetail>
        <div className="container-section mx-auto mt-32 px-6">
          <SectionBox
            className="md:flex md:flex-col md:justify-center md:min-h-[70vh]"
            titleClassName="text-2xl md:text-3xl text-white"
            background="radial-gradient(circle at 50% -100%, rgba(255,255,255,0) 0%, #123b7c 70%)"
          >
            <div className="flex justify-center">
              <div className="bg-white p-8 rounded-2xl shadow-md w-80 text-center">
                <h2 className="text-xl font-semibold mb-6 text-gray-800">
                  Sign up as
                </h2>

                <div className="flex flex-col gap-4">
                  <Button onClick={() => setRole("company")} className="w-full">
                    Company
                  </Button>

                  <Button
                    onClick={() => setRole("candidate")}
                    className="w-full bg-secondary hover:!bg-primary hover:!text-white !text-black"
                  >
                    Job Seeker
                  </Button>
                </div>

                <p className="mt-6 text-sm text-gray-500">
                  Already have an account?{" "}
                  <a
                    href={`/${currentLang}/login`}
                    className="text-primary hover:underline"
                  >
                    Login
                  </a>
                </p>
              </div>
            </div>
          </SectionBox>
        </div>
      </MainLayoutDetail>
    );
  }

  /* ================= REGISTER FORM ================= */
  return (
    <MainLayoutDetail>
      <div className="container-section mx-auto mt-32 px-6">
        <SectionBox
          className="md:flex md:flex-col md:justify-center md:min-h-[70vh]"
          titleClassName="text-2xl md:text-3xl text-white"
          background="radial-gradient(circle at 50% -100%, rgba(255,255,255,0) 0%, #123b7c 70%)"
        >
          <div className="flex justify-center">
            <form
              onSubmit={handleSubmit}
              className="bg-white p-4 md:p-8 rounded-2xl shadow-md w-96"
            >
              <h2 className="text-2xl font-semibold mb-6 text-center text-gray-800">
                Register as {role === "company" ? "Company" : "Job Seeker"}
              </h2>

              <div className="flex flex-col gap-4">
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  className="border rounded-lg p-3 text-sm"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />

                

                <div className="relative">

                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Password"
                    className="border rounded-lg p-3 text-sm w-full"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                

                <div className="relative">

                  <input
                    type={showPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  className="border rounded-lg p-3 text-sm w-full"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  minLength={8}
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                {/* <input
                  type="password"
                  name="confirmPassword"
                  placeholder="Confirm Password"
                  className="border rounded-lg p-3 text-sm"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  minLength={8}
                /> */}

                <div className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={agree}
                    disabled={!canAgree}
                    onChange={(e) => setAgree(e.target.checked)}
                    className="mt-1"
                  />

                  <span className="text-gray-600">
                    I agree to the{" "}
                    <button
                      type="button"
                      onClick={() => setShowAgreement(true)}
                      className="text-primary underline hover:text-secondary"
                    >
                      User Agreement
                    </button>
                  </span>
                </div>



                <ReCAPTCHA
                  sitekey="6LfPuzcsAAAAAItzND8FHtg9SJvsFhRNDZJmiMnH"
                  onChange={handleCaptchaChange}
                />


                {/* 🔥 BUTTON SUBMIT AMAN */}
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-secondary"
                >
                  {loading ? "Registering..." : "Sign Up"}
                </Button>
              </div>

              <button
                type="button"
                onClick={() => setRole(null)}
                className="mt-4 text-xs text-gray-400 hover:text-primary block mx-auto"
              >
                ← Back to role selection
              </button>
            </form>
            <UserAgreementModal
              show={showAgreement}
              onClose={() => setShowAgreement(false)}
              onAgree={() => {
                setAgree(true);
                setShowAgreement(false);
              }}
            />
          </div>
        </SectionBox>
      </div>
    </MainLayoutDetail>
  );
}
